import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import { fmtCount, fmtOpenRate } from "../format";
import { TRIGGER_KEYS } from "../contract";
import { crmHref } from "../paths";
import CrmCard from "../ui/CrmCard";
import CrmSwitch from "../ui/CrmSwitch";
import CrmButton from "../ui/CrmButton";
import { CRM } from "../../../constants/testIds/crm";

/** "Booking exists", "No activity for 60 days", "After 5 completed sessions" … */
const triggerLabel = (t, trigger, reviewThreshold) => {
  if (trigger?.type === "review_moment" && reviewThreshold) {
    return t("seq.trigger.reviewMomentAt", { n: reviewThreshold });
  }
  return TRIGGER_KEYS[trigger?.type] ? t(TRIGGER_KEYS[trigger.type], trigger.params) : trigger?.type;
};

/**
 * One sequence card (spec Screen 6): name, CrmSwitch (draft/active), "{trigger} · {n}
 * steps", "Sent {n} · Opened {p}%" — muted "Sent · Opened" while nothing has sent yet —
 * Edit and Duplicate.
 */
export default function SequenceCard({ sequence, onToggle, onDuplicate, reviewThreshold }) {
  const t = useT("crm");
  const tc = useT("common");
  const [busy, setBusy] = useState(false);
  const testKey = sequence.system_key || sequence.id;

  const flip = async (nextActive) => {
    setBusy(true);
    try {
      await onToggle(sequence.id, nextActive);
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const copy = await onDuplicate(sequence.id);
      toast.success(t("email.duplicated"));
      return copy;
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openRate = fmtOpenRate(sequence.stats?.sent, sequence.stats?.opened, undefined);

  return (
    <CrmCard data-testid={CRM.emailSeq(testKey)} className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-xl text-eh-ink">{sequence.name}</p>
        <CrmSwitch checked={sequence.status === "active"} disabled={busy} onCheckedChange={flip}
          data-testid={CRM.emailSeqToggle(testKey)} aria-label={sequence.name} />
      </div>
      <p className="text-sm text-eh-muted">
        {triggerLabel(t, sequence.trigger, reviewThreshold)}
        {" · "}
        {sequence.step_count === 1 ? t("email.stepCount.one", { n: sequence.step_count }) : t("email.stepCount.other", { n: fmtCount(sequence.step_count) })}
      </p>
      <p className="text-sm text-eh-muted">
        {openRate ? t("email.stats", { sent: fmtCount(sequence.stats.sent), opened: openRate }) : t("email.statsEmpty")}
      </p>
      <div className="flex items-center gap-4 pt-1">
        <CrmButton as={Link} to={crmHref(`/email/sequences/${sequence.id}`)} variant="link" size="sm" className="px-0"
          data-testid={CRM.emailSeqEdit(testKey)}>
          {t("email.edit")}
        </CrmButton>
        <CrmButton variant="link" size="sm" className="px-0" disabled={busy} onClick={duplicate} data-testid={CRM.emailSeqDuplicate(testKey)}>
          {t("email.duplicate")}
        </CrmButton>
      </div>
    </CrmCard>
  );
}
